import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { StepSection } from '@/components/step-section';
import { TextField } from '@/components/text-field';
import {
  ExperienceRepeater,
  experience_error,
  experience_payload,
  rows_from_saved,
  type ExperienceRow,
} from '@/components/experience-repeater';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import { use_draft } from '@/features/onboarding/hooks';
import { next_module_step_after } from '@/features/onboarding/module-step-router';
import {
  use_save_trainer_details,
  use_trainer_details,
} from '@/features/trainer/hooks';
import {
  PET_SPECIES_OPTIONS,
  TRAINER_GENDER_OPTIONS,
  TRAINER_SERVICE_RADIUS_OPTIONS,
  TRAINING_MODE_OPTIONS,
  type PetSpecies,
  type TrainerGender,
  type TrainerServiceRadius,
  type TrainingMode,
} from '@/features/trainer/types';
import { ChipMultiSelect } from '@/components/chip-multi-select';

type Form = {
  full_name: string;
  date_of_birth: string;
  gender: TrainerGender | '';
  training_mode: TrainingMode[];
  service_radius: TrainerServiceRadius;
  years_of_experience: string;
  trainer_for_species: PetSpecies[];
  experience: ExperienceRow[];
};

const EMPTY: Form = {
  full_name: '',
  date_of_birth: '',
  gender: '',
  training_mode: [],
  service_radius: 'KM_10',
  years_of_experience: '',
  trainer_for_species: [],
  experience: [],
};

function years_old(iso_date: string): number {
  const d = new Date(iso_date);
  if (Number.isNaN(d.getTime())) return NaN;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

/** Keys of `Form` whose value is an array — the multi-select pill fields. */
type ArrayKeys<T> = { [K in keyof T]: T[K] extends unknown[] ? K : never }[keyof T];

export function ModuleTrainerPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_trainer_details({ enabled: !!draft_q.data });
  const save_m = use_save_trainer_details();
  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
      return;
    }
    if (!draft_q.data.module_types.includes('TRAINER')) {
      navigate(next_module_step_after('TRAINER', draft_q.data.module_types), {
        replace: true,
      });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_form({
        full_name: saved_q.data.full_name,
        date_of_birth: saved_q.data.date_of_birth.slice(0, 10),
        gender: saved_q.data.gender ?? '',
        training_mode: saved_q.data.training_mode ?? [],
        service_radius: saved_q.data.service_radius,
        years_of_experience: String(saved_q.data.years_of_experience),
        trainer_for_species: saved_q.data.trainer_for_species,
        // Re-entering the form has to show what is stored, or a vendor who came back to
        // fix a typo would silently wipe their history by submitting an empty list.
        experience: rows_from_saved(saved_q.data.experience ?? []),
      });
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
  }

  /**
   * Toggle a pill *inside* the updater. Computing `toggle(form.x, v)` at the call
   * site reads the render closure, so several picks landing in one React batch
   * all start from the same stale array and only the last one survives.
   */
  function toggle_in<K extends ArrayKeys<Form>>(key: K, value: Form[K][number]) {
    set_form((f) => ({ ...f, [key]: toggle(f[key] as Form[K][number][], value) }));
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    if (form.full_name.trim().length < 2) {
      set_error('Full name must be at least 2 characters.');
      return;
    }
    if (!form.date_of_birth) {
      set_error('Date of birth is required.');
      return;
    }
    const age = years_old(form.date_of_birth);
    if (!Number.isFinite(age) || age < 18 || age > 100) {
      set_error('Trainer must be between 18 and 100 years old.');
      return;
    }
    const yoe = Number(form.years_of_experience);
    if (!Number.isInteger(yoe) || yoe < 0 || yoe > 80) {
      set_error('Years of experience must be a whole number between 0 and 80.');
      return;
    }
    if (form.trainer_for_species.length < 1) {
      set_error('Pick at least one species you train for.');
      return;
    }

    const experience_problem = experience_error(form.experience);
    if (experience_problem) {
      set_error(experience_problem);
      return;
    }

    try {
      await save_m.mutateAsync({
        full_name: form.full_name.trim(),
        date_of_birth: form.date_of_birth,
        gender: form.gender === '' ? undefined : form.gender,
        // Sent even when empty: an empty list is a real answer ("not decided yet"), and
        // omitting it would leave a previously-saved set of modes in place on re-submit.
        training_mode: form.training_mode,
        service_radius: form.service_radius,
        years_of_experience: yoe,
        trainer_for_species: form.trainer_for_species,
        experience: experience_payload(form.experience),
      });
      if (!draft_q.data) return;
      navigate(next_module_step_after('TRAINER', draft_q.data.module_types));
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save trainer details', { highlights_fields: true })
          : 'Could not save trainer details',
      );
    }
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="module-trainer"
        title="Pet Trainer details"
        description="Where you train, what species, and how long you've been at it."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="module-trainer-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <TextField
          label="Full name"
          name="full_name"
          error={field_errors.full_name}
          data-testid="field-trainer-full-name"
          value={form.full_name}
          onChange={(e) => update('full_name', e.target.value)}
          required
          minLength={2}
          maxLength={120}
          placeholder="Priya Iyer"
        />

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="date_of_birth">Date of birth</Label>
            <DatePicker
              id="date_of_birth"
              data-testid="field-trainer-dob"
              value={form.date_of_birth}
              onChange={(v) => update('date_of_birth', v)}
              max={new Date().toISOString().slice(0, 10)}
              placeholder="Select date of birth"
            />
          </div>
          <TextField
            label="Years of experience"
            name="years_of_experience"
            error={field_errors.years_of_experience}
            data-testid="field-trainer-yoe"
            value={form.years_of_experience}
            onChange={(e) =>
              update(
                'years_of_experience',
                e.target.value.replace(/\D/g, '').slice(0, 3),
              )
            }
            inputMode="numeric"
            required
            placeholder="4"
          />
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div className="flex flex-col gap-2">
            <Label htmlFor="gender">Gender</Label>
            <SelectField
              id="gender"
              data-testid="field-trainer-gender"
              value={form.gender}
              onValueChange={(v) => update('gender', v as TrainerGender | '')}
              options={[
                { value: '', label: '— Prefer not to say —' },
                ...TRAINER_GENDER_OPTIONS,
              ]}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="service_radius">Service radius</Label>
            <SelectField
              id="service_radius"
              data-testid="field-trainer-radius"
              value={form.service_radius}
              onValueChange={(v) => update('service_radius', v as TrainerServiceRadius)}
              options={TRAINER_SERVICE_RADIUS_OPTIONS}
            />
          </div>
        </div>

        <ChipMultiSelect
          label="Training mode"
          hint="pick any that apply"
          testid="trainer-mode"
          options={TRAINING_MODE_OPTIONS}
          selected={form.training_mode}
          on_toggle={(v) => toggle_in('training_mode', v)}
          disabled={save_m.isPending}
        />

        <ChipMultiSelect
          label="Species you train"
          hint="pick 1–6"
          testid="trainer-species"
          options={PET_SPECIES_OPTIONS}
          selected={form.trainer_for_species}
          on_toggle={(v) => toggle_in('trainer_for_species', v)}
          disabled={save_m.isPending}
        />

        <ExperienceRepeater
          noun="training their dog"
          testid_prefix="trainer"
          rows={form.experience}
          on_change={(rows) => update('experience', rows)}
          disabled={save_m.isPending}
        />
      </StepSection>
    </form>
  );
}
